import { AlignJustify, Clock, LayoutGrid, LucideIcon } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui';
import {
  MIGRATION_NEW_APP_SIGN_IN_URL,
  MIGRATION_WELCOME_DISMISSED_KEY,
} from '@/config/migration';
import { ROUTES } from '@/config/routes';
import { usePrivyAccount } from '@/hooks';
import { useMigrationStore } from '@/store/use-migration-store';
import { useModalStore } from '@/store/use-modal-store';

const BackgroundImage = ({ className }: { className?: string }) => {
  return (
    <svg
      width="709"
      height="515"
      viewBox="0 0 709 515"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <g filter="url(#filter0_ngf_1721_8488)">
        <ellipse
          cx="121.5"
          cy="10"
          rx="121.5"
          ry="10"
          transform="matrix(-1 0 0 1 316.002 182.046)"
          fill="#D9D9D9"
        />
      </g>
      <g filter="url(#filter1_gf_1721_8488)">
        <ellipse
          cx="58.0061"
          cy="1"
          rx="58.0061"
          ry="1"
          transform="matrix(-0.999909 0.0134936 0.0134936 0.999909 322.002 181)"
          fill="#D9D9D9"
        />
      </g>
      <g filter="url(#filter2_g_1721_8488)">
        <ellipse
          cx="58.0061"
          cy="1"
          rx="58.0061"
          ry="1"
          transform="matrix(-0.999909 0.0134936 0.0134936 0.999909 192 181)"
          fill="#D9D9D9"
        />
      </g>
      <g opacity="0.4" filter="url(#filter3_f_1721_8488)">
        <path
          d="M47.2734 -101L1.99928 -74.8609L175.052 181.272L185.326 178.034L47.2734 -101Z"
          fill="url(#paint0_linear_1721_8488)"
        />
      </g>
      <g opacity="0.3" filter="url(#filter4_f_1721_8488)">
        <rect
          width="17.7879"
          height="307.253"
          transform="matrix(-0.707107 0.707107 0.707107 0.707107 96.5781 -95)"
          fill="url(#paint1_linear_1721_8488)"
        />
      </g>
      <g filter="url(#filter5_f_1721_8488)">
        <ellipse
          cx="313.643"
          cy="245.872"
          rx="233.535"
          ry="97.4676"
          transform="rotate(9.95424 313.643 245.872)"
          fill="#65B482"
        />
      </g>
      <g filter="url(#filter6_f_1721_8488)">
        <circle cx="162.5" cy="90.5" r="0.5" fill="#D9D9D9" />
      </g>
      <g filter="url(#filter7_f_1721_8488)">
        <circle cx="190" cy="237" r="1" fill="#D9D9D9" />
      </g>
      <g filter="url(#filter8_f_1721_8488)">
        <circle
          cx="294.177"
          cy="130.826"
          r="0.825581"
          transform="rotate(180 294.177 130.826)"
          fill="#D9D9D9"
        />
      </g>
      <g filter="url(#filter9_f_1721_8488)">
        <circle cx="220" cy="181" r="1" fill="#D9D9D9" />
      </g>
      <g filter="url(#filter10_f_1721_8488)">
        <circle
          cx="269.409"
          cy="177.058"
          r="0.825581"
          transform="rotate(180 269.409 177.058)"
          fill="#D9D9D9"
        />
      </g>
      <g filter="url(#filter11_f_1721_8488)">
        <ellipse cx="234.5" cy="177" rx="0.5" ry="1" fill="#D9D9D9" />
      </g>
      <g filter="url(#filter12_f_1721_8488)">
        <ellipse
          cx="257.436"
          cy="180.36"
          rx="0.412791"
          ry="0.825581"
          transform="rotate(180 257.436 180.36)"
          fill="#D9D9D9"
        />
      </g>
      <g filter="url(#filter13_f_1721_8488)">
        <circle cx="253.5" cy="179.5" r="0.5" fill="#D9D9D9" />
      </g>
      <g filter="url(#filter14_f_1721_8488)">
        <circle
          cx="241.75"
          cy="178.296"
          r="0.412791"
          transform="rotate(180 241.75 178.296)"
          fill="#D9D9D9"
        />
      </g>
      <g filter="url(#filter15_f_1721_8488)">
        <circle cx="221.5" cy="164.5" r="0.5" fill="#D9D9D9" />
      </g>
      <g filter="url(#filter16_f_1721_8488)">
        <circle
          cx="268.171"
          cy="190.68"
          r="0.412791"
          transform="rotate(180 268.171 190.68)"
          fill="#D9D9D9"
        />
      </g>
      <g filter="url(#filter17_f_1721_8488)">
        <circle cx="251.5" cy="152.5" r="0.5" fill="#D9D9D9" />
      </g>
      <g filter="url(#filter18_f_1721_8488)">
        <circle
          cx="243.401"
          cy="200.587"
          r="0.412791"
          transform="rotate(180 243.401 200.587)"
          fill="#D9D9D9"
        />
      </g>
      <g filter="url(#filter19_f_1721_8488)">
        <circle cx="199.5" cy="178.5" r="0.5" fill="#D9D9D9" />
      </g>
      <g filter="url(#filter20_f_1721_8488)">
        <circle
          cx="286.334"
          cy="179.122"
          r="0.412791"
          transform="rotate(180 286.334 179.122)"
          fill="#D9D9D9"
        />
      </g>
      <g filter="url(#filter21_f_1721_8488)">
        <ellipse cx="207.5" cy="180.5" rx="1.5" ry="0.5" fill="#D9D9D9" />
      </g>
      <g filter="url(#filter22_f_1721_8488)">
        <ellipse
          cx="279.729"
          cy="177.471"
          rx="1.23837"
          ry="0.412791"
          transform="rotate(180 279.729 177.471)"
          fill="#D9D9D9"
        />
      </g>
      <g filter="url(#filter23_f_1721_8488)">
        <ellipse cx="247.5" cy="179.5" rx="1.5" ry="0.5" fill="#D9D9D9" />
      </g>
      <g filter="url(#filter24_f_1721_8488)">
        <ellipse
          cx="246.704"
          cy="178.296"
          rx="1.23837"
          ry="0.412791"
          transform="rotate(180 246.704 178.296)"
          fill="#D9D9D9"
        />
      </g>
      <g filter="url(#filter25_f_1721_8488)">
        <ellipse
          cx="208.386"
          cy="173.441"
          rx="1.5"
          ry="0.5"
          transform="rotate(47.2319 208.386 173.441)"
          fill="#D9D9D9"
        />
      </g>
      <g filter="url(#filter26_f_1721_8488)">
        <ellipse
          cx="278.998"
          cy="183.299"
          rx="1.23837"
          ry="0.412791"
          transform="rotate(-132.768 278.998 183.299)"
          fill="#D9D9D9"
        />
      </g>
      <defs>
        <filter
          id="filter0_ngf_1721_8488"
          x="67.0024"
          y="176.046"
          width="255"
          height="32"
          filterUnits="userSpaceOnUse"
          color-interpolation-filters="sRGB"
        >
          <feFlood flood-opacity="0" result="BackgroundImageFix" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="BackgroundImageFix"
            result="shape"
          />
          <feTurbulence
            type="fractalNoise"
            baseFrequency="0.18181818723678589 0.18181818723678589"
            stitchTiles="stitch"
            numOctaves="3"
            result="noise"
            seed="6139"
          />
          <feComponentTransfer in="noise" result="coloredNoise1">
            <feFuncR type="linear" slope="2" intercept="-0.5" />
            <feFuncG type="linear" slope="2" intercept="-0.5" />
            <feFuncB type="linear" slope="2" intercept="-0.5" />
            <feFuncA
              type="discrete"
              tableValues="1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 "
            />
          </feComponentTransfer>
          <feComposite
            operator="in"
            in2="shape"
            in="coloredNoise1"
            result="noise1Clipped"
          />
          <feComponentTransfer in="noise1Clipped" result="color1">
            <feFuncA type="table" tableValues="0 0.25" />
          </feComponentTransfer>
          <feMerge result="effect1_noise_1721_8488">
            <feMergeNode in="shape" />
            <feMergeNode in="color1" />
          </feMerge>
          <feTurbulence
            type="fractalNoise"
            baseFrequency="0.5 0.5"
            numOctaves="3"
            seed="3048"
          />
          <feDisplacementMap
            in="effect1_noise_1721_8488"
            scale="12"
            xChannelSelector="R"
            yChannelSelector="G"
            result="displacedImage"
            width="100%"
            height="100%"
          />
          <feMerge result="effect2_texture_1721_8488">
            <feMergeNode in="displacedImage" />
          </feMerge>
          <feGaussianBlur
            stdDeviation="1"
            result="effect3_foregroundBlur_1721_8488"
          />
        </filter>
        <filter
          id="filter1_gf_1721_8488"
          x="200.014"
          y="175.513"
          width="128.002"
          height="14.5397"
          filterUnits="userSpaceOnUse"
          color-interpolation-filters="sRGB"
        >
          <feFlood flood-opacity="0" result="BackgroundImageFix" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="BackgroundImageFix"
            result="shape"
          />
          <feTurbulence
            type="fractalNoise"
            baseFrequency="0.99900001287460327 0.99900001287460327"
            numOctaves="3"
            seed="3048"
          />
          <feDisplacementMap
            in="shape"
            scale="12"
            xChannelSelector="R"
            yChannelSelector="G"
            result="displacedImage"
            width="100%"
            height="100%"
          />
          <feMerge result="effect1_texture_1721_8488">
            <feMergeNode in="displacedImage" />
          </feMerge>
          <feGaussianBlur
            stdDeviation="0.5"
            result="effect2_foregroundBlur_1721_8488"
          />
        </filter>
        <filter
          id="filter2_g_1721_8488"
          x="70.0117"
          y="175.513"
          width="128.002"
          height="14.5397"
          filterUnits="userSpaceOnUse"
          color-interpolation-filters="sRGB"
        >
          <feFlood flood-opacity="0" result="BackgroundImageFix" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="BackgroundImageFix"
            result="shape"
          />
          <feTurbulence
            type="fractalNoise"
            baseFrequency="0.3333333432674408 0.3333333432674408"
            numOctaves="3"
            seed="3048"
          />
          <feDisplacementMap
            in="shape"
            scale="12"
            xChannelSelector="R"
            yChannelSelector="G"
            result="displacedImage"
            width="100%"
            height="100%"
          />
          <feMerge result="effect1_texture_1721_8488">
            <feMergeNode in="displacedImage" />
          </feMerge>
        </filter>
        <filter
          id="filter3_f_1721_8488"
          x="-40.3524"
          y="-143.352"
          width="268.03"
          height="366.975"
          filterUnits="userSpaceOnUse"
          color-interpolation-filters="sRGB"
        >
          <feFlood flood-opacity="0" result="BackgroundImageFix" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="BackgroundImageFix"
            result="shape"
          />
          <feGaussianBlur
            stdDeviation="21.1759"
            result="effect1_foregroundBlur_1721_8488"
          />
        </filter>
        <filter
          id="filter4_f_1721_8488"
          x="57.5301"
          y="-121.47"
          width="282.779"
          height="282.779"
          filterUnits="userSpaceOnUse"
          color-interpolation-filters="sRGB"
        >
          <feFlood flood-opacity="0" result="BackgroundImageFix" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="BackgroundImageFix"
            result="shape"
          />
          <feGaussianBlur
            stdDeviation="13.235"
            result="effect1_foregroundBlur_1721_8488"
          />
        </filter>
        <filter
          id="filter5_f_1721_8488"
          x="-81.0024"
          y="-22.2975"
          width="789.291"
          height="536.34"
          filterUnits="userSpaceOnUse"
          color-interpolation-filters="sRGB"
        >
          <feFlood flood-opacity="0" result="BackgroundImageFix" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="BackgroundImageFix"
            result="shape"
          />
          <feGaussianBlur
            stdDeviation="82"
            result="effect1_foregroundBlur_1721_8488"
          />
        </filter>
        <filter
          id="filter6_f_1721_8488"
          x="161"
          y="89"
          width="3"
          height="3"
          filterUnits="userSpaceOnUse"
          color-interpolation-filters="sRGB"
        >
          <feFlood flood-opacity="0" result="BackgroundImageFix" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="BackgroundImageFix"
            result="shape"
          />
          <feGaussianBlur
            stdDeviation="0.5"
            result="effect1_foregroundBlur_1721_8488"
          />
        </filter>
        <filter
          id="filter7_f_1721_8488"
          x="187"
          y="234"
          width="6"
          height="6"
          filterUnits="userSpaceOnUse"
          color-interpolation-filters="sRGB"
        >
          <feFlood flood-opacity="0" result="BackgroundImageFix" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="BackgroundImageFix"
            result="shape"
          />
          <feGaussianBlur
            stdDeviation="1"
            result="effect1_foregroundBlur_1721_8488"
          />
        </filter>
        <filter
          id="filter8_f_1721_8488"
          x="291.7"
          y="128.349"
          width="4.95369"
          height="4.95351"
          filterUnits="userSpaceOnUse"
          color-interpolation-filters="sRGB"
        >
          <feFlood flood-opacity="0" result="BackgroundImageFix" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="BackgroundImageFix"
            result="shape"
          />
          <feGaussianBlur
            stdDeviation="0.825581"
            result="effect1_foregroundBlur_1721_8488"
          />
        </filter>
        <filter
          id="filter9_f_1721_8488"
          x="218"
          y="179"
          width="4"
          height="4"
          filterUnits="userSpaceOnUse"
          color-interpolation-filters="sRGB"
        >
          <feFlood flood-opacity="0" result="BackgroundImageFix" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="BackgroundImageFix"
            result="shape"
          />
          <feGaussianBlur
            stdDeviation="0.5"
            result="effect1_foregroundBlur_1721_8488"
          />
        </filter>
        <filter
          id="filter10_f_1721_8488"
          x="267.758"
          y="175.407"
          width="3.30253"
          height="3.30235"
          filterUnits="userSpaceOnUse"
          color-interpolation-filters="sRGB"
        >
          <feFlood flood-opacity="0" result="BackgroundImageFix" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="BackgroundImageFix"
            result="shape"
          />
          <feGaussianBlur
            stdDeviation="0.412791"
            result="effect1_foregroundBlur_1721_8488"
          />
        </filter>
        <filter
          id="filter11_f_1721_8488"
          x="233"
          y="175"
          width="3"
          height="4"
          filterUnits="userSpaceOnUse"
          color-interpolation-filters="sRGB"
        >
          <feFlood flood-opacity="0" result="BackgroundImageFix" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="BackgroundImageFix"
            result="shape"
          />
          <feGaussianBlur
            stdDeviation="0.5"
            result="effect1_foregroundBlur_1721_8488"
          />
        </filter>
        <filter
          id="filter12_f_1721_8488"
          x="256.197"
          y="178.709"
          width="2.47685"
          height="3.30235"
          filterUnits="userSpaceOnUse"
          color-interpolation-filters="sRGB"
        >
          <feFlood flood-opacity="0" result="BackgroundImageFix" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="BackgroundImageFix"
            result="shape"
          />
          <feGaussianBlur
            stdDeviation="0.412791"
            result="effect1_foregroundBlur_1721_8488"
          />
        </filter>
        <filter
          id="filter13_f_1721_8488"
          x="252"
          y="178"
          width="3"
          height="3"
          filterUnits="userSpaceOnUse"
          color-interpolation-filters="sRGB"
        >
          <feFlood flood-opacity="0" result="BackgroundImageFix" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="BackgroundImageFix"
            result="shape"
          />
          <feGaussianBlur
            stdDeviation="0.5"
            result="effect1_foregroundBlur_1721_8488"
          />
        </filter>
        <filter
          id="filter14_f_1721_8488"
          x="240.511"
          y="177.058"
          width="2.47685"
          height="2.47672"
          filterUnits="userSpaceOnUse"
          color-interpolation-filters="sRGB"
        >
          <feFlood flood-opacity="0" result="BackgroundImageFix" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="BackgroundImageFix"
            result="shape"
          />
          <feGaussianBlur
            stdDeviation="0.412791"
            result="effect1_foregroundBlur_1721_8488"
          />
        </filter>
        <filter
          id="filter15_f_1721_8488"
          x="220"
          y="163"
          width="3"
          height="3"
          filterUnits="userSpaceOnUse"
          color-interpolation-filters="sRGB"
        >
          <feFlood flood-opacity="0" result="BackgroundImageFix" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="BackgroundImageFix"
            result="shape"
          />
          <feGaussianBlur
            stdDeviation="0.5"
            result="effect1_foregroundBlur_1721_8488"
          />
        </filter>
        <filter
          id="filter16_f_1721_8488"
          x="266.933"
          y="189.442"
          width="2.47685"
          height="2.47672"
          filterUnits="userSpaceOnUse"
          color-interpolation-filters="sRGB"
        >
          <feFlood flood-opacity="0" result="BackgroundImageFix" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="BackgroundImageFix"
            result="shape"
          />
          <feGaussianBlur
            stdDeviation="0.412791"
            result="effect1_foregroundBlur_1721_8488"
          />
        </filter>
        <filter
          id="filter17_f_1721_8488"
          x="250"
          y="151"
          width="3"
          height="3"
          filterUnits="userSpaceOnUse"
          color-interpolation-filters="sRGB"
        >
          <feFlood flood-opacity="0" result="BackgroundImageFix" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="BackgroundImageFix"
            result="shape"
          />
          <feGaussianBlur
            stdDeviation="0.5"
            result="effect1_foregroundBlur_1721_8488"
          />
        </filter>
        <filter
          id="filter18_f_1721_8488"
          x="242.163"
          y="199.349"
          width="2.47685"
          height="2.47672"
          filterUnits="userSpaceOnUse"
          color-interpolation-filters="sRGB"
        >
          <feFlood flood-opacity="0" result="BackgroundImageFix" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="BackgroundImageFix"
            result="shape"
          />
          <feGaussianBlur
            stdDeviation="0.412791"
            result="effect1_foregroundBlur_1721_8488"
          />
        </filter>
        <filter
          id="filter19_f_1721_8488"
          x="197"
          y="176"
          width="5"
          height="5"
          filterUnits="userSpaceOnUse"
          color-interpolation-filters="sRGB"
        >
          <feFlood flood-opacity="0" result="BackgroundImageFix" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="BackgroundImageFix"
            result="shape"
          />
          <feGaussianBlur
            stdDeviation="1"
            result="effect1_foregroundBlur_1721_8488"
          />
        </filter>
        <filter
          id="filter20_f_1721_8488"
          x="284.27"
          y="177.058"
          width="4.12801"
          height="4.12789"
          filterUnits="userSpaceOnUse"
          color-interpolation-filters="sRGB"
        >
          <feFlood flood-opacity="0" result="BackgroundImageFix" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="BackgroundImageFix"
            result="shape"
          />
          <feGaussianBlur
            stdDeviation="0.825581"
            result="effect1_foregroundBlur_1721_8488"
          />
        </filter>
        <filter
          id="filter21_f_1721_8488"
          x="204"
          y="178"
          width="7"
          height="5"
          filterUnits="userSpaceOnUse"
          color-interpolation-filters="sRGB"
        >
          <feFlood flood-opacity="0" result="BackgroundImageFix" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="BackgroundImageFix"
            result="shape"
          />
          <feGaussianBlur
            stdDeviation="1"
            result="effect1_foregroundBlur_1721_8488"
          />
        </filter>
        <filter
          id="filter22_f_1721_8488"
          x="276.84"
          y="175.407"
          width="5.77889"
          height="4.12789"
          filterUnits="userSpaceOnUse"
          color-interpolation-filters="sRGB"
        >
          <feFlood flood-opacity="0" result="BackgroundImageFix" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="BackgroundImageFix"
            result="shape"
          />
          <feGaussianBlur
            stdDeviation="0.825581"
            result="effect1_foregroundBlur_1721_8488"
          />
        </filter>
        <filter
          id="filter23_f_1721_8488"
          x="244"
          y="177"
          width="7"
          height="5"
          filterUnits="userSpaceOnUse"
          color-interpolation-filters="sRGB"
        >
          <feFlood flood-opacity="0" result="BackgroundImageFix" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="BackgroundImageFix"
            result="shape"
          />
          <feGaussianBlur
            stdDeviation="1"
            result="effect1_foregroundBlur_1721_8488"
          />
        </filter>
        <filter
          id="filter24_f_1721_8488"
          x="243.814"
          y="176.233"
          width="5.77889"
          height="4.12789"
          filterUnits="userSpaceOnUse"
          color-interpolation-filters="sRGB"
        >
          <feFlood flood-opacity="0" result="BackgroundImageFix" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="BackgroundImageFix"
            result="shape"
          />
          <feGaussianBlur
            stdDeviation="0.825581"
            result="effect1_foregroundBlur_1721_8488"
          />
        </filter>
        <filter
          id="filter25_f_1721_8488"
          x="206.303"
          y="171.288"
          width="4.16602"
          height="4.30524"
          filterUnits="userSpaceOnUse"
          color-interpolation-filters="sRGB"
        >
          <feFlood flood-opacity="0" result="BackgroundImageFix" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="BackgroundImageFix"
            result="shape"
          />
          <feGaussianBlur
            stdDeviation="0.5"
            result="effect1_foregroundBlur_1721_8488"
          />
        </filter>
        <filter
          id="filter26_f_1721_8488"
          x="277.278"
          y="181.522"
          width="3.43974"
          height="3.55436"
          filterUnits="userSpaceOnUse"
          color-interpolation-filters="sRGB"
        >
          <feFlood flood-opacity="0" result="BackgroundImageFix" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="BackgroundImageFix"
            result="shape"
          />
          <feGaussianBlur
            stdDeviation="0.412791"
            result="effect1_foregroundBlur_1721_8488"
          />
        </filter>
        <linearGradient
          id="paint0_linear_1721_8488"
          x1="78.2093"
          y1="-42.7471"
          x2="144"
          y2="111"
          gradientUnits="userSpaceOnUse"
        >
          <stop stop-color="white" />
          <stop offset="1" stop-color="white" stop-opacity="0.1" />
        </linearGradient>
        <linearGradient
          id="paint1_linear_1721_8488"
          x1="0.794563"
          y1="65.9164"
          x2="29.3734"
          y2="188.206"
          gradientUnits="userSpaceOnUse"
        >
          <stop stop-color="white" />
          <stop offset="1" stop-color="white" stop-opacity="0.1" />
        </linearGradient>
      </defs>
    </svg>
  );
};

const FEATURES: { icon: LucideIcon; title: string; text: string }[] = [
  {
    icon: AlignJustify,
    title: 'migration.welcome.strategyTitle',
    text: 'migration.welcome.strategyText',
  },
  {
    icon: LayoutGrid,
    title: 'migration.welcome.interfaceTitle',
    text: 'migration.welcome.interfaceText',
  },
  {
    icon: Clock,
    title: 'migration.welcome.executionTitle',
    text: 'migration.welcome.executionText',
  },
];

const dismissedKey = (address: string | undefined) =>
  address
    ? `${MIGRATION_WELCOME_DISMISSED_KEY}:${address.toLowerCase()}`
    : MIGRATION_WELCOME_DISMISSED_KEY;

const readDismissed = (address: string | undefined) => {
  try {
    return localStorage.getItem(dismissedKey(address)) === 'true';
  } catch {
    return false;
  }
};

const writeDismissed = (address: string | undefined) => {
  try {
    localStorage.setItem(dismissedKey(address), 'true');
  } catch {
    // storage unavailable — the modal comes back next visit
  }
};

export default function MigrationWelcomeModal() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const status = useMigrationStore((state) => state.status);
  const isAccountEmpty = useMigrationStore((state) => state.isAccountEmpty);
  const { address: primaryAddress } = usePrivyAccount();
  const isOpen = useModalStore((state) => state.isMigrationWelcomeOpen);

  const shouldOffer =
    status !== null && !status.started && pathname !== ROUTES.MIGRATION;

  useEffect(() => {
    if (shouldOffer && !readDismissed(primaryAddress)) {
      useModalStore.setState({ isMigrationWelcomeOpen: true });
    }
  }, [shouldOffer, primaryAddress]);

  const dismiss = () => {
    writeDismissed(primaryAddress);
    useModalStore.setState({ isMigrationWelcomeOpen: false });
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) dismiss();
      }}
    >
      <DialogContent
        className="no-scrollbar max-h-[calc(100dvh-2rem)] overflow-y-auto overflow-x-hidden border-none bg-background p-0 sm:max-w-6xl sm:rounded-3xl"
        closeClassName="right-6 top-6 sm:right-10 sm:top-10"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <div className="relative pb-8 sm:pb-12">
          <BackgroundImage className="pointer-events-none absolute inset-x-0 top-0 h-[551px]" />

          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 top-0 h-[420px]"
            style={{
              background:
                'linear-gradient(to bottom, rgb(var(--primary) / 0.06) 0%, transparent 50%)',
            }}
          />

          <DialogHeader className="relative px-6 pt-14 sm:pt-20">
            <DialogTitle
              className="bg-clip-text text-center text-3xl font-semibold !leading-tight tracking-wide text-transparent sm:text-5xl"
              style={{
                backgroundImage:
                  'linear-gradient(140deg, hsl(var(--foreground)) 40%, hsl(var(--background)) 140%)',
              }}
            >
              {t('migration.welcome.title')}
            </DialogTitle>

            <p className="relative z-10 mx-auto !mt-8 max-w-[68ch] text-center text-base leading-snug text-muted-foreground sm:text-lg">
              {t('migration.welcome.subtitle')}
            </p>

            <DialogDescription className="sr-only">
              {t('migration.welcome.description')}
            </DialogDescription>
          </DialogHeader>

          <div className="relative mx-auto mt-10 w-[calc(100%-8rem)]">
            <img
              src="/new-vest.png"
              alt="Vest 2.0"
              className="inset-x-0 top-0 w-full"
            />
          </div>

          <div
            className="pointer-events-none absolute inset-x-0 top-0 h-full"
            style={{
              background:
                'linear-gradient(to bottom, transparent 0%, transparent 45%, hsl(var(--background)) 50%)',
            }}
          />

          <div className="max-sm:glass relative z-10 mx-12 -mt-2 max-sm:rounded-3xl max-sm:bg-background/100 max-sm:p-8 sm:-mt-10 md:-mt-20">
            <ul className="grid gap-8 sm:grid-cols-3 sm:gap-12 md:gap-16">
              {FEATURES.map(({ icon: Icon, title, text }) => (
                <li key={title} className="flex flex-col gap-6">
                  <span className="flex size-12 items-center justify-center rounded-xl border border-green/20 bg-green/5 text-green">
                    <Icon size={20} aria-hidden="true" />
                  </span>
                  <div className="flex flex-col gap-3">
                    <h3 className="text-xl font-semibold leading-tight">
                      {t(title)}
                    </h3>
                    <p className="text-lg leading-normal text-muted-foreground">
                      {t(text)}
                    </p>
                  </div>
                </li>
              ))}
            </ul>

            <p className="mt-12 whitespace-pre-line text-center text-base leading-relaxed text-muted-foreground">
              {t(
                isAccountEmpty
                  ? 'migration.welcome.emptyNote'
                  : 'migration.welcome.note',
              )}
            </p>

            <div className="mb-4 mt-8 flex w-full flex-col items-center justify-center gap-2">
              <Button
                type="button"
                variant="contrast"
                size="lg"
                className="h-16 w-full rounded-2xl sm:w-3/5"
                style={{
                  backgroundImage:
                    'linear-gradient(140deg, hsl(var(--foreground)) 40%, hsl(var(--background)) 140%)',
                }}
                asChild={isAccountEmpty}
                onClick={
                  isAccountEmpty
                    ? dismiss
                    : () => {
                        dismiss();
                        navigate(ROUTES.MIGRATION);
                      }
                }
              >
                {isAccountEmpty ? (
                  <a
                    href={MIGRATION_NEW_APP_SIGN_IN_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('migration.welcome.tradeNow')}
                  </a>
                ) : (
                  t('migration.welcome.cta')
                )}
              </Button>

              {/* <Button
                type="button"
                variant="ghost"
                size="lg"
                className="h-16 w-full rounded-2xl text-muted-foreground sm:w-3/5"
                asChild
              >
                <a
                  href={MIGRATION_CHANGES_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={dismiss}
                >
                  {t('migration.welcome.readMore')}
                </a>
              </Button> */}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
